/**
 * DocumentProgress Component - Document checklist progress
 */

import React from 'react'
import { Card, Badge } from '../ui'
import { FileCheck, FileX, Clock, AlertTriangle } from 'lucide-react'

const DocumentProgress = ({
  documents = [],
  total = 0,
  onViewAll,
  className = '',
  ...props
}) => {
  const uploaded = documents.filter(d => d.status === 'uploaded' || d.status === 'verified').length
  const verified = documents.filter(d => d.status === 'verified').length
  const missing = documents.filter(d => d.status === 'missing').length
  const pending = documents.filter(d => d.status === 'pending' || d.status === 'awaiting').length
  
  const pct = total > 0 ? Math.round((uploaded / total) * 100) : 0

  const stats = [
    { label: 'Uploaded', value: uploaded, icon: FileCheck, color: 'text-success' },
    { label: 'Missing', value: missing, icon: FileX, color: 'text-error' },
    { label: 'Pending', value: pending, icon: Clock, color: 'text-warning' },
    { label: 'Verified', value: verified, icon: AlertTriangle, color: 'text-brand' },
  ]

  return (
    <Card className={`p-6 ${className}`} {...props}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-ink">Document Checklist</h3>
          <p className="text-xs text-muted mt-0.5">
            {uploaded} of {total} documents uploaded
          </p>
        </div>
        {onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-xs font-semibold text-brand hover:text-brand-dark transition-colors"
          >
            View All →
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-brand rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Stats grid */}
      <div className="mt-4 grid grid-cols-4 gap-2">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Icon size={12} className={stat.color} />
                <span className="text-sm font-bold text-ink">{stat.value}</span>
              </div>
              <p className="text-[10px] text-muted mt-0.5">{stat.label}</p>
            </div>
          )
        })}
      </div>

      {/* Recent documents */}
      {documents.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">
            Recent uploads
          </p>
          <div className="space-y-1.5">
            {documents.slice(0, 3).map((doc) => (
              <div key={doc.id} className="flex items-center justify-between text-xs">
                <span className="text-ink truncate flex-1">{doc.label}</span>
                <Badge 
                  variant={
                    doc.status === 'verified' ? 'success' :
                    doc.status === 'uploaded' ? 'primary' :
                    doc.status === 'pending' ? 'warning' : 'default'
                  }
                  size="sm"
                >
                  {doc.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

DocumentProgress.displayName = 'DocumentProgress'

export default DocumentProgress